import AddIcon from "@mui/icons-material/Add";
import ApartmentIcon from "@mui/icons-material/Apartment";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Alert,
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import useBoolean from "hooks/useBoolean";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useParams } from "react-router-dom";
import {
  ListRequestOfRequirementAdminFilter,
  RequirementSetAction,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps, Severity } from "types/common-types";
import translateError from "utils/translateError";
import ensureOk from "./ensureOk";
import NewRequirementSetDialog from "./NewRequirementSetDialog";
import PublishBar from "./PublishBar";
import RequirementDialog from "./RequirementDialog";
import RequirementPane from "./RequirementPane";
import RequirementSetPane from "./RequirementSetPane";
import TrackerAvailabilityPane from "./TrackerAvailabilityPane";
import { isEditableStatus, statusOf } from "./requirementSetStatus";
import styles from "./styles";
import authoringPermissions from "./authoringPermissions";

type Selection =
  | { kind: "set" }
  | { kind: "tracker" }
  | { kind: "requirement"; requirementId: string };

/**
 * Author & Publish: one screen for a licence's rules and their sign-off. Everything happens in place —
 * the rail switches which editor is shown and nothing navigates away.
 */
const RequirementAuthoring = () => {
  const { formatMessage: t } = useIntl();
  const { licenseId } = useParams<{ licenseId: string }>();
  const {
    licenseService,
    requirementSetService,
    requirementService,
    licensesUrl,
    licenseDetailsUrl,
  } = useGlobalContext();

  const queryClient = useQueryClient();
  const permissions = authoringPermissions();
  const [selection, setSelection] = useState<Selection>({ kind: "set" });
  const [selectedSetId, setSelectedSetId] = useState<string>();
  const [snackbarMessage, setSnackbarMessage] =
    useState<SnackbarMessageProps | null>(null);
  const [busy, busyActions] = useBoolean(false);
  const [newSetOpen, newSetActions] = useBoolean(false);
  const [addRequirementOpen, addRequirementActions] = useBoolean(false);

  const licenseResult = useQuery(
    ["licenseService.getLicenseById", licenseId],
    () => licenseService.getLicenseById(licenseId!),
    { enabled: !!licenseId }
  );

  const setsResult = useQuery(
    ["requirementSetService.getRequirementSetsByLicense", licenseId],
    () => requirementSetService.getRequirementSetsByLicense(licenseId!),
    { enabled: !!licenseId }
  );

  const optionsResult = useQuery(
    ["requirementService.getAuthoringOptions"],
    () => requirementService.getAuthoringOptions()
  );

  const sets = useMemo(() => setsResult.data?.data ?? [], [setsResult.data]);

  // The newest set is the one an author almost always means, so it opens selected. Moving to another
  // licence has to clear the selection, or this screen would keep editing the previous licence's set.
  useEffect(() => {
    setSelectedSetId(undefined);
    setSelection({ kind: "set" });
  }, [licenseId]);

  useEffect(() => {
    if (!selectedSetId && sets.length > 0) {
      setSelectedSetId(sets[0].id);
    }
  }, [sets, selectedSetId]);

  const setDetailResult = useQuery(
    ["requirementSetService.getRequirementSetById", selectedSetId],
    () => requirementSetService.getRequirementSetById(selectedSetId!),
    { enabled: !!selectedSetId }
  );

  const requirementsResult = useQuery(
    ["requirementService.getRequirementsBySet", selectedSetId],
    () =>
      requirementService.getRequirementsBySet(
        selectedSetId!,
        ListRequestOfRequirementAdminFilter.fromJS({
          pageSize: -1,
        })
      ),
    { enabled: !!selectedSetId }
  );

  const set = setDetailResult.data?.data;
  const requirements = requirementsResult.data?.data ?? [];
  const license = licenseResult.data?.data;

  useTitle(set?.name ?? "Author & Publish");

  /**
   * Queries are cached with staleTime Infinity, so a refetch of this screen's three keys is not
   * enough: approving a set changes the incumbent's end date, whether the licence is trackable, and
   * what the License page shows. Everything derived from the licence is invalidated by prefix.
   */
  const refreshSet = () => {
    queryClient.invalidateQueries(["requirementSetService.getRequirementSetById"]);
    queryClient.invalidateQueries(["requirementSetService.getRequirementSetsByLicense"]);
    queryClient.invalidateQueries(["requirementSetService.getActiveRequirementSet"]);
    queryClient.invalidateQueries(["requirementService.getRequirementsBySet"]);
    queryClient.invalidateQueries(["requirementService.getRequirementById"]);
    queryClient.invalidateQueries(["requirementService.getRequirementsByLicense"]);
    queryClient.invalidateQueries(["licenseTrackerService.getLicenseTrackerAvailability"]);
  };

  const selectSet = (requirementSetId: string) => {
    setSelectedSetId(requirementSetId);
    setSelection({ kind: "set" });
  };

  const runTransition = async (action: RequirementSetAction) => {
    if (!set?.id) return;

    busyActions.setTrue();

    try {
      switch (action) {
        case RequirementSetAction.SubmitForReview:
          ensureOk(await requirementSetService.submitRequirementSetForReview(set.id));
          break;
        case RequirementSetAction.Approve:
          ensureOk(await requirementSetService.approveRequirementSet(set.id));
          break;
        case RequirementSetAction.ReturnToDraft:
          ensureOk(await requirementSetService.returnRequirementSetToDraft(set.id));
          break;
        case RequirementSetAction.Archive:
          ensureOk(await requirementSetService.archiveRequirementSet(set.id));
          break;
        case RequirementSetAction.Unarchive:
          ensureOk(await requirementSetService.unarchiveRequirementSet(set.id));
          break;
      }

      setSnackbarMessage(new SnackbarMessageProps(t({ id: "requirementSetUpdated" })));
      refreshSet();
    } catch (error) {
      setSnackbarMessage(
        new SnackbarMessageProps(translateError(error, t), Severity.Error)
      );
    } finally {
      busyActions.setFalse();
    }
  };

  if (!licenseId) return <Navigate to={licensesUrl()} replace />;

  // The set detail is waited for too: without it the heading would read "no requirement sets" for one
  // render pass on a licence that has several.
  if (
    licenseResult.isLoading ||
    setsResult.isLoading ||
    (!!selectedSetId && setDetailResult.isLoading)
  ) {
    return <Loader />;
  }

  const locked = !set || !isEditableStatus(statusOf(set.statusEnumValue)) || !permissions.canEdit;
  const selectedIndex =
    selection.kind === "requirement"
      ? requirements.findIndex((r) => r.id === selection.requirementId)
      : -1;

  return (
    <>
      <RedirectBackButton
        redirectRoute={licenseDetailsUrl(licenseId)}
        labelKey="backToLicense"
        labelValues={{ license: license?.displayName ?? "" }}
      />

      <Typography variant="h5">{set?.name ?? t({ id: "noRequirementSets" })}</Typography>
      <Typography variant="body2" color="text.secondary">
        {license?.displayName}
      </Typography>

      {sets.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t({ id: "noRequirementSetsForLicense" })}
          </Alert>
          {permissions.canEdit && (
            <Box sx={styles.transitions}>
              <IconButton onClick={newSetActions.setTrue}>
                <AddIcon />
              </IconButton>
              <Typography variant="body2" alignSelf="center">
                {t({ id: "newRequirementSet" })}
              </Typography>
            </Box>
          )}
        </Paper>
      ) : (
        set && (
          <>
            <PublishBar
              set={set}
              sets={sets}
              permissions={permissions}
              busy={busy}
              onSelectSet={selectSet}
              onNewSet={newSetActions.setTrue}
              onTransition={runTransition}
            />

            <Box sx={styles.workbench}>
              <Paper variant="outlined" sx={styles.rail}>
                <List disablePadding dense>
                  <ListItemButton
                    selected={selection.kind === "set"}
                    onClick={() => setSelection({ kind: "set" })}
                  >
                    <ListItemIcon>
                      <TuneIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t({ id: "requirementSet" })}
                      secondary={t({ id: "cycleCreditCarryover" })}
                    />
                  </ListItemButton>

                  <ListItemButton
                    selected={selection.kind === "tracker"}
                    onClick={() => setSelection({ kind: "tracker" })}
                  >
                    <ListItemIcon>
                      <ApartmentIcon />
                    </ListItemIcon>
                    <ListItemText primary={t({ id: "trackerAvailability" })} />
                  </ListItemButton>
                </List>

                <Divider />

                <Box sx={styles.railGroupHeader}>
                  <Typography variant="subtitle2">
                    {t(
                      { id: "requirementsCount" },
                      { count: requirements.length }
                    )}
                  </Typography>
                  {!locked && (
                    <Tooltip title={t({ id: "addRequirement" })}>
                      <IconButton
                        size="small"
                        onClick={addRequirementActions.setTrue}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {requirements.length === 0 ? (
                  <Box p={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t({ id: "noRequirementsInSet" })}
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding dense>
                    {requirements.map((requirement) => (
                      <ListItemButton
                        key={requirement.id}
                        selected={
                          selection.kind === "requirement" &&
                          selection.requirementId === requirement.id
                        }
                        onClick={() =>
                          setSelection({
                            kind: "requirement",
                            requirementId: requirement.id!,
                          })
                        }
                      >
                        <ListItemIcon>
                          <Avatar sx={styles.requirementCircle}>
                            {requirement.sortOrder}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={requirement.name}
                          secondary={`${requirement.ruleTypeName} ${requirement.amount} · ${requirement.recurrenceTypeName}`}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Paper>

              <Box>
                {selection.kind === "set" && (
                  <RequirementSetPane
                    set={set}
                    options={optionsResult.data?.data}
                    locked={locked}
                    onSaved={() => {
                      setSnackbarMessage(
                        new SnackbarMessageProps(t({ id: "requirementSetSaved" }))
                      );
                      refreshSet();
                    }}
                    onNotesChanged={refreshSet}
                    onError={(message) =>
                      setSnackbarMessage(
                        new SnackbarMessageProps(message, Severity.Error)
                      )
                    }
                  />
                )}

                {selection.kind === "tracker" && (
                  <TrackerAvailabilityPane
                    licenseId={licenseId}
                    canSetAvailability={permissions.canSetAvailability}
                  />
                )}

                {selection.kind === "requirement" && (
                  <RequirementPane
                    requirementId={selection.requirementId}
                    requirementSetId={set.id!}
                    options={optionsResult.data?.data}
                    locked={locked}
                    hasPrevious={selectedIndex > 0}
                    hasNext={
                      selectedIndex >= 0 && selectedIndex < requirements.length - 1
                    }
                    onPrevious={() =>
                      setSelection({
                        kind: "requirement",
                        requirementId: requirements[selectedIndex - 1].id!,
                      })
                    }
                    onNext={() =>
                      setSelection({
                        kind: "requirement",
                        requirementId: requirements[selectedIndex + 1].id!,
                      })
                    }
                    onChanged={refreshSet}
                    onDeleted={() => {
                      setSelection({ kind: "set" });
                      refreshSet();
                    }}
                    onError={(message) =>
                      setSnackbarMessage(
                        new SnackbarMessageProps(message, Severity.Error)
                      )
                    }
                  />
                )}
              </Box>
            </Box>
          </>
        )
      )}

      {newSetOpen && (
        <NewRequirementSetDialog
          licenseId={licenseId}
          licenseDisplayName={license?.displayName}
          sets={sets}
          open
          onClose={newSetActions.setFalse}
          onCreated={(requirementSetId) => {
            newSetActions.setFalse();
            setSnackbarMessage(
              new SnackbarMessageProps(t({ id: "requirementSetCreated" }))
            );
            setsResult.refetch();
            selectSet(requirementSetId);
          }}
        />
      )}

      {addRequirementOpen && set?.id && (
        <RequirementDialog
          requirementSetId={set.id}
          options={optionsResult.data?.data}
          open
          onClose={addRequirementActions.setFalse}
          onSaved={(requirementId) => {
            addRequirementActions.setFalse();
            setSnackbarMessage(
              new SnackbarMessageProps(t({ id: "requirementSaved" }))
            );
            requirementsResult.refetch();
            if (requirementId) {
              setSelection({ kind: "requirement", requirementId });
            }
          }}
        />
      )}

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default RequirementAuthoring;
